import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../../api/apiClient';
import Modal from '../../../components/Modal';
import WorkScheduleEffectiveFields from './WorkScheduleEffectiveFields';
import UserEffectiveHistoryFields from './UserEffectiveHistoryFields';
import ResignDateModal from './ResignDateModal';
import { BulkModal } from './BulkRegisterModal';
import UserDetailFields, { UserContactFields } from './UserDetailFields';
import { WorkTypeQuickAddPanel } from './WorkTypeQuickAddPanel';
import DepartmentSelect from './DepartmentSelect';
import LabeledField, { labeledInputStyle, labeledSelectStyle } from './LabeledField';
import { formGridStyle, helperTextStyle } from './styles';
import {
  dateLabelStyle, futureSchedulePanelStyle, futureScheduleTitleStyle,
  futureScheduleTextStyle, futureScheduleCancelBtnStyle,
} from './userModalStyles';
import { stdBlackBtn, stdOutlineBtn } from '../adminlayout/designStandard';
import { isNoId, normalizeAttendanceTimeType, toDate } from './utils';

// 3.3% 부서 목록에 지금 부서가 없으면(이름이 바뀌기 전 기록 등) 그 값도 보여서, 상자가 빈칸으로 보이지 않게
function withCurrent(names, current) {
  const list = Array.isArray(names) ? names : [];
  return current && !list.includes(current) ? [...list, current] : list;
}

// 비밀번호 칸(2026-11-05 맵찔찔이님 그림) — 실제 비밀번호를 보여 주는 칸이 아니고 입력도 안 된다.
//   수정 = **** + [초기화](확인 후 바로 처리, 창은 그대로) / 등록 = 초기 비밀번호 0214, [초기화] 못 누름.
//   <label> 안에 버튼을 두면 눌림이 칸으로 넘어갈 수 있어 LabeledField 대신 같은 모양의 div.
function PasswordField({ editHook }) {
  const { editing, resetPassword } = editHook;
  return (
    <div style={{ position: 'relative' }}>
      <span style={dateLabelStyle}>비밀번호</span>
      <input
        style={{ ...labeledInputStyle(), paddingRight: 70, cursor: 'default' }}
        aria-label="비밀번호"
        value={editing ? '****' : '0214'}
        readOnly
        tabIndex={-1}
      />
      <button
        type="button"
        style={{ ...passwordResetBtnStyle, ...(editing ? {} : { opacity: 0.45, cursor: 'not-allowed' }) }}
        disabled={!editing}
        onClick={() => resetPassword(editing)}
      >
        초기화
      </button>
    </div>
  );
}

// 연차 시작 차수 옆 칸 — 할 수 있는 일은 목록과 같은 판정(rowActions).
//   4대보험 = 퇴직처리(이미 퇴직 = '퇴직 완료' 못 누름, 되돌리기는 퇴사일자 칸을 비우고 저장) / 3.3% = 담당 지정된 줄만 담당 해제(퇴직은 퇴사일자 칸, 1a).
//   등록 창 = 아직 없는 사람이라 퇴직처리는 못 누름, 3.3% 를 고르면 비어 있다.
//   할 일이 없어도 빈 칸 자리는 차지한다 — 없으면 격자가 아래 은행명을 이 자리로 끌어올린다(formGridDenseStyle).
function AccountActionCell({ editHook, actions }) {
  const { editing, isMarketerForm, deactivateFromEdit, unlinkFromEdit } = editHook;
  if (!editing) {
    if (isMarketerForm) return <div />;
    return <button type="button" style={{ ...actionCellBtnStyle, opacity: 0.45, cursor: 'not-allowed' }} disabled>퇴직처리</button>;
  }
  if (actions?.resign) {
    if (editing.resign_date || !editing.is_active) {
      const date = toDate(editing.resign_date);
      return <button type="button" style={resignedCellStyle} disabled>퇴직 완료{date ? ` (${date})` : ''}</button>;
    }
    return <button type="button" style={actionCellBtnStyle} onClick={deactivateFromEdit}>퇴직처리</button>;
  }
  if (actions?.unlink) return <button type="button" style={actionCellBtnStyle} onClick={unlinkFromEdit}>담당 해제</button>;
  return <div />;
}

// '+ 새 부서 추가' 줄(한 줄 전체)이 부서 바로 뒤에 끼어도 근무유형이 부서 오른쪽 자리에 그대로 있게 빈자리를 채운다.
const formGridDenseStyle = { ...formGridStyle, gridAutoFlow: 'dense' };

const passwordResetBtnStyle = {
  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', padding: '3px 8px',
  border: '1px solid var(--text-muted)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text)', fontSize: 12, cursor: 'pointer',
};
const actionCellBtnStyle = {
  width: '100%', height: 43, border: 'none', borderRadius: 7, background: 'var(--border)', color: 'var(--text)', fontSize: 13, fontWeight: 700, cursor: 'pointer',
};
const resignedCellStyle = {
  ...actionCellBtnStyle, border: '1px solid var(--border)', background: 'var(--bg-subtle)', color: 'var(--text-muted)', cursor: 'default',
};

function EditModal({ editHook, entityLabel, onCreateAttendanceType, ipAliases, departmentSettings, freelancerDepartmentSettings, accountActions }) {
  const { open, setOpen, editing, form, setForm, saveUser, cancelFutureWorkSchedule, openAddressSearch, isMarketerForm } = editHook;
  const { attendanceTypeOptions, departments } = editHook;
  const nextWorkSchedule = editing?.next_work_schedule;
  const nextWorkScheduleType = normalizeAttendanceTimeType(nextWorkSchedule?.attendance_time_type, attendanceTypeOptions) || '출퇴근 없음';

  // '+ 새 근무유형 추가' 선택 시 오른쪽 설정 패널. 모달이 닫히면 패널 표시 상태도 초기화.
  const [addingType, setAddingType] = useState(false);
  useEffect(() => { if (!open) setAddingType(false); }, [open]);
  // 세션 가드: 저장 중 모달을 닫고 다른 직원을 열면 늦은 성공 응답이 그 직원 폼을 덮어쓸 수
  //   있어, 요청 시작 시점의 대상이 그대로일 때만 폼에 반영한다(유형 생성 자체는 회사 설정이라 유지).
  const formSessionRef = useRef(0);
  useEffect(() => { formSessionRef.current += 1; }, [open, editing]);
  async function submitNewType(template) {
    const session = formSessionRef.current;
    const result = await onCreateAttendanceType(template);
    if (result?.ok && formSessionRef.current === session) {
      setForm((prev) => ({ ...prev, attendance_time_type: result.name }));
      setAddingType(false);
    }
    return result;
  }

  // 테넌트 관리자는 근무유형 필수(2026-08-17 확정 정책) — '출퇴근 없음' 선택지 자체를 숨긴다.
  //   근무유형이 비면 연차·출퇴근 달력 대상에서 제외되는데, 테넌트 화면엔 그 사실을 알 길이 없어
  //   "직원관리엔 있는데 연차엔 안 나오는" 증상을 만들었다. 서버(user.route/user.create)도 같은 검증을 한다.
  const [isTenantAdmin, setIsTenantAdmin] = useState(false);
  useEffect(() => {
    let active = true;
    apiClient.get('/api/me').then((r) => {
      if (active) setIsTenantAdmin(Boolean(r.data?.user?.isTenantAdmin));
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  return (
    <>
      <Modal
      open={open}
      title={editing ? `${entityLabel} 수정` : `${entityLabel} 등록`}
      icon={editing ? '✏️' : '👤'}
      onClose={() => setOpen(false)}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={stdOutlineBtn} onClick={() => setOpen(false)}>취소</button>
          <button style={stdBlackBtn} onClick={saveUser}>저장</button>
        </div>
      )}
    >
      <div style={formGridDenseStyle}>
        {/* 구분(2026-11-03 한 목록) — 새로 등록할 때만, 매번 고른다(결정 3b). 등록 뒤에는 바꾸지 않는다(계정을 새로). */}
        {!editing && (
          <LabeledField label="구분" select style={{ gridColumn: '1 / -1' }}>
            <select
              style={{ ...labeledSelectStyle, fontWeight: 700 }}
              aria-label="구분"
              value={form.create_kind || ''}
              onChange={(e) => setForm((prev) => ({ ...prev, create_kind: e.target.value, department: '' }))}
            >
              <option value="" disabled>선택 (필수)</option>
              <option value="insured">4대보험 — 급여명세서·출퇴근</option>
              {editHook.canCreateFreelancer && <option value="freelance">3.3% — 정산서·출퇴근 없음</option>}
            </select>
          </LabeledField>
        )}
        <LabeledField label="아이디">
          <input
            style={labeledInputStyle()}
            placeholder={editing && isNoId(editing.user_id) ? '최초 설정' : '선택'}
            value={form.userId}
            disabled={Boolean(editing) && !isNoId(editing?.user_id)}
            onChange={(e) => setForm({ ...form, userId: e.target.value })}
          />
        </LabeledField>
        <PasswordField editHook={editHook} />
        <LabeledField label="이름*">
          <input
            style={labeledInputStyle()}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </LabeledField>
        <UserContactFields form={form} setForm={setForm} />
        {/* 부서: 4대보험 = 이 회사 부서 / 3.3% = 테넌트의 3.3% 부서 목록(2026-11-03 합치기-2 — 예전엔 '마케팅' 고정이라 칸이 없었다).
            3.3% 도 부서는 필수(결정 5b). 3.3% 는 근무유형(출퇴근)이 없어 아래 근무유형 칸은 그대로 숨긴다(useUserEdit 가 저장 때 뺀다). */}
        <DepartmentSelect
          value={form.department}
          onChange={(department) => setForm((prev) => ({ ...prev, department }))}
          departments={isMarketerForm ? withCurrent(freelancerDepartmentSettings?.departmentNames, form.department) : departments}
          settings={isMarketerForm ? freelancerDepartmentSettings : departmentSettings}
          open={open}
        />
        {/* 마케터 단말기 ID (2026-10-21) — 마케팅명부 화면을 없애며 빠졌던 칸. 값은 그 화면 마케터 탭 열과 같다
            (본사 화면=본사 매출 카드승인 단말기, 회사 화면=그 회사 단말기 — marketerTerminal.js). 3.3% 는 근무유형 자리에. */}
        {isMarketerForm && (
          <LabeledField label="단말기 ID">
            <input
              style={labeledInputStyle()}
              placeholder="카드승인과 맞추는 번호"
              value={form.terminal_id || ''}
              maxLength={120}
              onChange={(e) => setForm({ ...form, terminal_id: e.target.value })}
            />
          </LabeledField>
        )}
        {!isMarketerForm && (
          <LabeledField label="근무유형" select>
            <select
              style={labeledSelectStyle}
              value={normalizeAttendanceTimeType(form.attendance_time_type, attendanceTypeOptions)}
              onChange={(e) => {
                if (e.target.value === '__add__') { setAddingType(true); return; }
                setForm({ ...form, attendance_time_type: e.target.value || null });
              }}
            >
              {isTenantAdmin
                ? <option value="" disabled>선택 (필수)</option>
                : <option value="">없음(출퇴근 없음)</option>}
              {attendanceTypeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
              {/* 모달에서 바로 근무유형 추가 (2026-08-27 사용자 명세) — 기본 일정(월~금 09~18시)으로 생성, 시간 설정에서 수정 가능 */}
              {onCreateAttendanceType && <option value="__add__">+ 새 근무유형 추가</option>}
            </select>
          </LabeledField>
        )}
        {/* 부서를 바꾸면 뜨는 적용일 칸 — 부서·근무유형이 한 줄에 놓이게 근무유형 뒤에 둔다 */}
        <UserEffectiveHistoryFields
          kind="department"
          editing={editing}
          form={form}
          setForm={setForm}
        />
        {!isMarketerForm && (
          <>
            {isTenantAdmin && !addingType && !normalizeAttendanceTimeType(form.attendance_time_type, attendanceTypeOptions) && (
              <div style={helperTextStyle}>근무유형은 필수입니다 — 미선택 시 연차·출퇴근 관리 대상에서 제외됩니다.</div>
            )}
            <WorkScheduleEffectiveFields
              editing={editing}
              form={form}
              setForm={setForm}
              attendanceTypeOptions={attendanceTypeOptions}
            />
          </>
        )}
        {editing && nextWorkSchedule?.id && (
          <div style={futureSchedulePanelStyle}>
            <div>
              <div style={futureScheduleTitleStyle}>예약된 근무유형 변경</div>
              <div style={futureScheduleTextStyle}>
                {nextWorkSchedule.effective_from}부터 {nextWorkScheduleType} 적용 예정
              </div>
            </div>
            <button type="button" style={futureScheduleCancelBtnStyle} onClick={cancelFutureWorkSchedule}>
              예약 취소
            </button>
          </div>
        )}
        <UserDetailFields
          form={form}
          setForm={setForm}
          editing={editing}
          openAddressSearch={openAddressSearch}
          actionCell={<AccountActionCell editHook={editHook} actions={accountActions} />}
        />
      </div>
      </Modal>
      <WorkTypeQuickAddPanel
        open={open && addingType}
        onClose={() => setAddingType(false)}
        onSubmit={submitNewType}
        existingNames={attendanceTypeOptions}
        ipAliases={ipAliases}
      />
    </>
  );
}


export function UserModals({ editHook, bulkHook, showBulk, entityLabel, departmentSettings, freelancerDepartmentSettings, accountActions, onCreateAttendanceType, ipAliases }) {
  return (
    <>
      <EditModal editHook={editHook} entityLabel={entityLabel} onCreateAttendanceType={onCreateAttendanceType} ipAliases={ipAliases} departmentSettings={departmentSettings} freelancerDepartmentSettings={freelancerDepartmentSettings} accountActions={accountActions} />
      <ResignDateModal editHook={editHook} entityLabel={entityLabel} />
      {showBulk && <BulkModal bulkHook={bulkHook} departments={departmentSettings?.departmentNames || []} />}
    </>
  );
}
